import { ref } from 'vue'
import { defineStore, acceptHMRUpdate } from 'pinia'
import type { Notification } from '@/types'

let notificationId = 0

export const useNotificationStore = defineStore('notification', () => {
  const notifications = ref<Notification[]>([])

  function show(notification: Omit<Notification, 'id'>): string {
    const id = `notification-${++notificationId}`
    const entry: Notification = {
      id,
      dismissible: true,
      duration: 5000,
      ...notification,
    }

    notifications.value.push(entry)

    if (entry.duration && entry.duration > 0) {
      setTimeout(() => dismiss(id), entry.duration)
    }

    return id
  }

  function success(title: string, message?: string): string {
    return show({ type: 'success', title, message })
  }

  function error(title: string, message?: string): string {
    return show({ type: 'error', title, message, duration: 8000 })
  }

  function warning(title: string, message?: string): string {
    return show({ type: 'warning', title, message })
  }

  function info(title: string, message?: string): string {
    return show({ type: 'info', title, message })
  }

  function dismiss(id: string): void {
    notifications.value = notifications.value.filter((n) => n.id !== id)
  }

  function dismissAll(): void {
    notifications.value = []
  }

  return {
    notifications,
    show,
    success,
    error,
    warning,
    info,
    dismiss,
    dismissAll,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useNotificationStore, import.meta.hot))
}
